import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RolPlataforma } from '../../../../../core/models/novedades.models';
import { DatosPersonales } from '../../../../../core/models/perfil.models';
import {
  InstitucionFormulario,
  PresupuestoInstitucionFormulario,
  PresupuestoItemFormulario,
  PresupuestoSubitemFormulario,
} from '../../../../../core/models/postulaciones.models';
import { FormularioEditable } from '../postulacion-formulario.models';

@Component({
  selector: 'app-postulacion-paso-presupuesto',
  imports: [CommonModule, FormsModule],
  templateUrl: './postulacion-paso-presupuesto.html',
  styleUrls: ['./postulacion-formulario-paso.shared.css', './postulacion-paso-presupuesto.css'],
})
export class PostulacionPasoPresupuesto implements OnChanges {
  @Input({ required: true }) formulario!: FormularioEditable;
  @Input() soloLectura = false;
  @Input() guardando = false;
  @Input() mensajeGuardado = '';
  @Input() rol: RolPlataforma = 'postulante';
  @Input() perfil: DatosPersonales | null = null;
  @Output() changed = new EventEmitter<void>();
  @Output() saveRequested = new EventEmitter<void>();

  presupuestoDetalleActivo: PresupuestoInstitucionFormulario | null = null;
  private presupuestosDesplegados = new Set<string>();
  private claveDetalleActivo: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mensajeGuardado'] && this.mensajeGuardado) {
      this.cerrarDetallePresupuesto();
      return;
    }

    if (changes['formulario'] && this.formulario) {
      this.asegurarPresupuestoPorInstitucion();
    }

    if ((changes['formulario'] || changes['guardando']) && this.presupuestoDetalleActivo) {
      this.reenlazarDetalleActivo();
    }
  }

  get aniosPresupuesto(): number[] {
    const cantidadAnios = Number(this.formulario?.duracionPresupuestoAnios) || 3;
    return Array.from({ length: Math.max(1, Math.trunc(cantidadAnios)) }, (_, index) => index + 1);
  }

  get presupuestosPorInstitucion(): PresupuestoInstitucionFormulario[] {
    return this.formulario.presupuestoPorInstitucion ?? [];
  }

  registrarCambio(): void {
    this.sincronizarTotalesPlanos();
    this.changed.emit();
  }

  montoSubitem(subitem: PresupuestoSubitemFormulario, anioIndex: number): number {
    return Number(subitem.montosPorAnio?.[anioIndex]) || 0;
  }

  actualizarMontoSubitem(subitem: PresupuestoSubitemFormulario, anioIndex: number, valor: string | number): void {
    const montos = subitem.montosPorAnio ?? this.obtenerMontosPersistentes(subitem);
    montos[anioIndex] = Number(valor) || 0;
    subitem.montosPorAnio = montos;
    this.registrarCambio();
  }

  totalSubitem(subitem: PresupuestoSubitemFormulario): number {
    return this.aniosPresupuesto.reduce((total, _, index) => total + this.montoSubitem(subitem, index), 0);
  }

  totalItem(item: PresupuestoItemFormulario): number {
    return (item.subitems ?? []).reduce((total, subitem) => total + this.totalSubitem(subitem), 0);
  }

  totalInstitucion(presupuesto: PresupuestoInstitucionFormulario): number {
    return (presupuesto.items ?? []).reduce((total, item) => total + this.totalItem(item), 0);
  }

  totalGeneral(): number {
    return (this.formulario.presupuestoPorInstitucion ?? []).reduce(
      (total, presupuesto) => total + this.totalInstitucion(presupuesto),
      0
    );
  }

  totalCategoria(nombre: string): number {
    const presupuestos = this.formulario.presupuestoPorInstitucion ?? [];
    return presupuestos
      .flatMap((presupuesto) => presupuesto.items ?? [])
      .flatMap((item) => item.subitems ?? [])
      .filter((subitem) => subitem.nombre === nombre)
      .reduce((total, subitem) => total + this.totalSubitem(subitem), 0);
  }

  esItemEquipamiento(item: PresupuestoItemFormulario): boolean {
    return this.normalizarTexto(item.item) === 'equipamiento';
  }

  esItemGastosPersonal(item: PresupuestoItemFormulario): boolean {
    return this.normalizarTexto(item.item) === 'gastos en personal';
  }

  agregarLineaEquipamiento(item: PresupuestoItemFormulario): void {
    if (this.soloLectura || !this.esItemEquipamiento(item)) return;

    const siguiente = (item.subitems ?? []).length + 1;
    item.subitems = item.subitems ?? [];
    item.subitems.push(this.crearSubitem(`Equipamiento ${siguiente}`, 0));
    this.registrarCambio();
  }

  puedeEliminarLineaEquipamiento(item: PresupuestoItemFormulario, subitemIndex: number): boolean {
    return !this.soloLectura && this.esItemEquipamiento(item) && subitemIndex > 0;
  }

  eliminarLineaEquipamiento(item: PresupuestoItemFormulario, subitemIndex: number): void {
    if (!this.puedeEliminarLineaEquipamiento(item, subitemIndex)) return;

    item.subitems = (item.subitems ?? []).filter((_, index) => index !== subitemIndex);
    this.registrarCambio();
  }

  actualizarNombreSubitem(subitem: PresupuestoSubitemFormulario, valor: string): void {
    subitem.nombre = String(valor ?? '').trimStart();
    this.registrarCambio();
  }

  etiquetaSubitem(item: PresupuestoItemFormulario, subitem: PresupuestoSubitemFormulario): string {
    if (this.esItemGastosPersonal(item) && this.normalizarTexto(subitem.nombre) === 'investigador(a) responsable') {
      const postulante = this.nombrePostulantePresupuesto();
      return postulante ? `Investigador(a) Responsable: ${postulante}` : 'Investigador(a) Responsable';
    }

    return subitem.nombre;
  }

  estaResumenAbierto(presupuesto: PresupuestoInstitucionFormulario, index: number): boolean {
    return this.presupuestosDesplegados.has(this.claveVistaPresupuesto(presupuesto, index));
  }

  alternarResumen(presupuesto: PresupuestoInstitucionFormulario, index: number): void {
    const clave = this.claveVistaPresupuesto(presupuesto, index);

    if (this.presupuestosDesplegados.has(clave)) {
      this.presupuestosDesplegados.delete(clave);
      return;
    }

    this.presupuestosDesplegados.add(clave);
  }

  abrirDetallePresupuesto(presupuesto: PresupuestoInstitucionFormulario): void {
    this.presupuestoDetalleActivo = presupuesto;
    this.claveDetalleActivo = this.clavePresupuesto(presupuesto);
  }

  cerrarDetallePresupuesto(): void {
    this.presupuestoDetalleActivo = null;
    this.claveDetalleActivo = null;
  }

  solicitarGuardadoPresupuesto(): void {
    if (this.soloLectura || this.guardando) return;

    this.registrarCambio();
    this.saveRequested.emit();
  }

  etiquetaTipoInstitucion(rol: PresupuestoInstitucionFormulario['rolInstitucion']): string {
    return rol === 'principal' ? 'Principal' : 'Secundaria';
  }

  trackByPresupuesto(index: number, presupuesto: PresupuestoInstitucionFormulario): string {
    return `${index}-${presupuesto.nombreInstitucion}-${presupuesto.unidadInstitucional}`;
  }

  trackByItem(index: number, item: PresupuestoItemFormulario): string {
    return `${index}-${item.item}`;
  }

  trackBySubitem(index: number, subitem: PresupuestoSubitemFormulario): string {
    return `${index}`;
  }

  private asegurarPresupuestoPorInstitucion(): void {
    if (!this.formulario) return;

    const instituciones = this.institucionesRegistradas();

    if (instituciones.length === 0) {
      this.formulario.presupuestoPorInstitucion = [];
      this.formulario.presupuestoItems = [];
      this.sincronizarTotalesPlanos();
      return;
    }

    const existentes = this.formulario.presupuestoPorInstitucion ?? [];
    const legacyItems = this.clonarItems(this.formulario.presupuestoItems ?? []);
    const hayTotalesPlanos = this.tieneTotalesPlanos();

    this.formulario.presupuestoPorInstitucion = instituciones.map((institucion, index) => {
      const existente = this.buscarPresupuestoExistente(institucion, existentes);
      const itemsExistentes = existente?.items?.length ? this.clonarItems(existente.items) : [];
      const itemsLegacy = index === 0 && legacyItems.length ? this.clonarItems(legacyItems) : [];
      const itemsBase = itemsExistentes.length
        ? itemsExistentes
        : itemsLegacy.length
          ? itemsLegacy
          : this.crearItemsBase(index === 0 && hayTotalesPlanos);

      const presupuesto = existente ?? {
        nombreInstitucion: institucion.nombreInstitucion,
        unidadInstitucional: institucion.unidadInstitucional,
        departamentoFacultad: institucion.departamentoFacultad,
        rolInstitucion: institucion.rol,
        items: [],
      };

      presupuesto.nombreInstitucion = institucion.nombreInstitucion;
      presupuesto.unidadInstitucional = institucion.unidadInstitucional;
      presupuesto.departamentoFacultad = institucion.departamentoFacultad;
      presupuesto.rolInstitucion = institucion.rol;
      presupuesto.items = this.normalizarItems(itemsBase);

      return presupuesto;
    });

    this.sincronizarTotalesPlanos();
  }

  private normalizarPresupuestos(): void {
    const presupuestos = this.formulario.presupuestoPorInstitucion ?? [];

    presupuestos.forEach((presupuesto) => {
      presupuesto.items = this.normalizarItems(presupuesto.items ?? []);
    });

    this.formulario.presupuestoPorInstitucion = presupuestos;
  }

  private normalizarItems(items: PresupuestoItemFormulario[]): PresupuestoItemFormulario[] {
    return items.map((item) => this.normalizarItemPresupuesto(item));
  }

  private obtenerMontosPersistentes(subitem: PresupuestoSubitemFormulario): number[] {
    const montosActuales = subitem.montosPorAnio ?? [];
    const cantidad = Math.max(this.aniosPresupuesto.length, montosActuales.length);
    return Array.from({ length: cantidad }, (_, index) => Number(montosActuales[index]) || 0);
  }

  private crearItemsBase(usarTotalesPlanos = false): PresupuestoItemFormulario[] {
    const monto = (valor: number): number => (usarTotalesPlanos ? Number(valor) || 0 : 0);

    return [
      {
        item: 'Gastos en Personal',
        subitems: [
          this.crearSubitem('Investigador(a) Responsable', monto(this.formulario.recursosHumanos)),
          this.crearSubitem('Coinvestigador(a)', 0),
          this.crearSubitem('Becas para Tesistas y/o Memoristas', 0),
          this.crearSubitem('Personal Técnico', 0),
          this.crearSubitem('Personal de Apoyo', 0),
        ],
      },
      {
        item: 'Gastos de Operación',
        subitems: [
          this.crearSubitem('Operación y funcionamiento', monto(this.formulario.operacionFuncionamiento)),
          this.crearSubitem('Viajes y viáticos', monto(this.formulario.viajesViaticos)),
          this.crearSubitem('Publicaciones y difusión', monto(this.formulario.publicacionesDifusion)),
          this.crearSubitem('Servicios externos', monto(this.formulario.serviciosExternos)),
          this.crearSubitem('Software y licencias', monto(this.formulario.softwareLicencias)),
          this.crearSubitem('Capacitación y formación', monto(this.formulario.capacitacionFormacion)),
          this.crearSubitem('Materiales e insumos', monto(this.formulario.materialesInsumos)),
        ],
      },
      {
        item: 'Infraestructura y Mobiliario',
        subitems: [this.crearSubitem('Infraestructura', monto(this.formulario.infraestructura))],
      },
      {
        item: 'Equipamiento',
        subitems: [this.crearSubitem('Equipamiento', monto(this.formulario.equipamiento))],
      },
    ];
  }

  private crearSubitem(nombre: string, totalActual: number): PresupuestoSubitemFormulario {
    return {
      nombre,
      montosPorAnio: this.aniosPresupuesto.map((_, index) => (index === 0 ? Number(totalActual) || 0 : 0)),
    };
  }

  private sincronizarTotalesPlanos(): void {
    const todosLosItems = (this.formulario.presupuestoPorInstitucion ?? []).flatMap((presupuesto) => presupuesto.items ?? []);
    const todosLosSubitems = todosLosItems.flatMap((item) => item.subitems ?? []);
    const totalPorNombre = (nombre: string): number =>
      todosLosSubitems
        .filter((subitem) => this.normalizarTexto(subitem.nombre) === this.normalizarTexto(nombre))
        .reduce((total, subitem) => total + this.totalSubitem(subitem), 0);

    this.formulario.presupuestoItems = this.clonarItems(this.formulario.presupuestoPorInstitucion[0]?.items ?? []);
    this.formulario.recursosHumanos = this.totalPorItem('Gastos en Personal');
    this.formulario.equipamiento = this.totalPorItem('Equipamiento');
    this.formulario.infraestructura = totalPorNombre('Infraestructura');
    this.formulario.operacionFuncionamiento = totalPorNombre('Operacion y funcionamiento');
    this.formulario.viajesViaticos = totalPorNombre('Viajes y viaticos');
    this.formulario.publicacionesDifusion = totalPorNombre('Publicaciones y difusion');
    this.formulario.serviciosExternos = totalPorNombre('Servicios externos');
    this.formulario.softwareLicencias = totalPorNombre('Software y licencias');
    this.formulario.capacitacionFormacion = totalPorNombre('Capacitacion y formacion');
    this.formulario.materialesInsumos = totalPorNombre('Materiales e insumos');
    this.formulario.totalSolicitado = this.totalGeneral();
  }

  private totalPorItem(nombre: string): number {
    return (this.formulario.presupuestoPorInstitucion ?? [])
      .flatMap((presupuesto) => presupuesto.items ?? [])
      .filter((item) => this.normalizarTexto(item.item) === this.normalizarTexto(nombre))
      .reduce((total, item) => total + this.totalItem(item), 0);
  }

  private institucionesRegistradas(): InstitucionFormulario[] {
    return (this.formulario.instituciones ?? [])
      .map((institucion, index): InstitucionFormulario => ({
        nombreInstitucion: String(institucion.nombreInstitucion ?? '').trim(),
        unidadInstitucional: String(institucion.unidadInstitucional ?? '').trim(),
        departamentoFacultad: String(institucion.departamentoFacultad ?? '').trim(),
        rol: index === 0 ? 'principal' : institucion.rol === 'principal' ? 'asociada' : institucion.rol,
      }))
      .filter((institucion) =>
        [institucion.nombreInstitucion, institucion.unidadInstitucional, institucion.departamentoFacultad].every(
          (valor) => valor.length > 0 && valor.toLowerCase() !== 'por definir'
        )
      );
  }

  private buscarPresupuestoExistente(
    institucion: InstitucionFormulario,
    existentes: PresupuestoInstitucionFormulario[]
  ): PresupuestoInstitucionFormulario | undefined {
    const clave = this.claveInstitucion(institucion);
    return (
      existentes.find((presupuesto) => this.clavePresupuesto(presupuesto) === clave) ??
      existentes.find((presupuesto) => presupuesto.nombreInstitucion === institucion.nombreInstitucion)
    );
  }

  private clonarItems(items: PresupuestoItemFormulario[]): PresupuestoItemFormulario[] {
    return items.map((item) => ({
      item: item.item,
      subitems: (item.subitems ?? []).map((subitem) => ({
        nombre: subitem.nombre,
        montosPorAnio: (subitem.montosPorAnio ?? []).map((monto) => Number(monto) || 0),
      })),
    }));
  }

  private claveInstitucion(institucion: InstitucionFormulario): string {
    return [institucion.nombreInstitucion, institucion.unidadInstitucional, institucion.departamentoFacultad]
      .map((valor) => String(valor ?? '').trim().toLowerCase())
      .join('|');
  }

  private clavePresupuesto(presupuesto: PresupuestoInstitucionFormulario): string {
    return [presupuesto.nombreInstitucion, presupuesto.unidadInstitucional, presupuesto.departamentoFacultad]
      .map((valor) => String(valor ?? '').trim().toLowerCase())
      .join('|');
  }

  private normalizarTexto(valor: string): string {
    return String(valor ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private claveVistaPresupuesto(presupuesto: PresupuestoInstitucionFormulario, index: number): string {
    return `${index}-${this.clavePresupuesto(presupuesto)}`;
  }

  private reenlazarDetalleActivo(): void {
    if (!this.claveDetalleActivo) return;

    const presupuestoActualizado = (this.formulario.presupuestoPorInstitucion ?? []).find(
      (presupuesto) => this.clavePresupuesto(presupuesto) === this.claveDetalleActivo
    );

    if (presupuestoActualizado) {
      this.presupuestoDetalleActivo = presupuestoActualizado;
    }
  }

  private tieneTotalesPlanos(): boolean {
    return [
      this.formulario.recursosHumanos,
      this.formulario.equipamiento,
      this.formulario.infraestructura,
      this.formulario.operacionFuncionamiento,
      this.formulario.viajesViaticos,
      this.formulario.publicacionesDifusion,
      this.formulario.serviciosExternos,
      this.formulario.softwareLicencias,
      this.formulario.capacitacionFormacion,
      this.formulario.materialesInsumos,
    ].some((valor) => Number(valor) > 0);
  }

  private normalizarItemPresupuesto(item: PresupuestoItemFormulario): PresupuestoItemFormulario {
    const itemNormalizado: PresupuestoItemFormulario = {
      ...item,
      subitems: (item.subitems ?? []).map((subitem) => ({
        ...subitem,
        montosPorAnio: this.obtenerMontosPersistentes(subitem),
      })),
    };

    if (this.esItemGastosPersonal(itemNormalizado)) {
      itemNormalizado.subitems = this.normalizarSubitemsGastosPersonal(itemNormalizado.subitems);
    }

    return itemNormalizado;
  }

  private normalizarSubitemsGastosPersonal(subitems: PresupuestoSubitemFormulario[]): PresupuestoSubitemFormulario[] {
    const requeridos = [
      'Investigador(a) Responsable',
      'Coinvestigador(a)',
      'Becas para Tesistas y/o Memoristas',
      'Personal Técnico',
      'Personal de Apoyo',
    ];

    const antiguosRecursosHumanos = subitems.find((subitem) => this.normalizarTexto(subitem.nombre) === 'recursos humanos');

    return requeridos.map((nombre, index) => {
      const existente = subitems.find((subitem) => this.normalizarTexto(subitem.nombre) === this.normalizarTexto(nombre));
      const origen = existente ?? (index === 0 ? antiguosRecursosHumanos : undefined);
      return {
        nombre,
        montosPorAnio: origen ? this.obtenerMontosPersistentes(origen) : this.crearSubitem(nombre, 0).montosPorAnio,
      };
    });
  }

  private nombrePostulantePresupuesto(): string {
    return [
      this.perfil?.primerNombre,
      this.perfil?.segundoNombre,
      this.perfil?.primerApellido,
      this.perfil?.segundoApellido,
    ]
      .filter((valor) => String(valor ?? '').trim().length > 0)
      .join(' ')
      .trim();
  }
}
